import * as React from "react";
import { useRef } from "react";
import { ChatMessage } from "../../../types/chatTypes";
import type { ChatStep } from "../../../types/stream.types";
import { Source } from "../../../../../types";
import BotMessage from "../../molecules/BotMessage";
import UserMessage from "../../molecules/UserMessage";
import TypingIndicator from "../../atoms/TypingIndicator";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ChatMessagesProps {
  messages: ChatMessage[];
  streamingMessage: string;
  isLoading: boolean;
  onFeedback: (messageId: string, feedback: 'like' | 'dislike') => void;
  onUpdateChangesStatus?: (messageId: string, status: 'applied' | 'cancelled' | 'accepted' | 'rejected') => Promise<void>;
  currentStreamingThoughts?: { agent: string; thought: string }[];
  onViewDocument?: (url: string, name: string) => void;
  messagesEndRef?: React.RefObject<HTMLDivElement>;
  onRetryMessage?: (messageId: string) => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({ 
  messages, 
  streamingMessage, 
  isLoading, 
  onFeedback,
  onUpdateChangesStatus,
  currentStreamingThoughts = [],
  onViewDocument,
  messagesEndRef: externalMessagesEndRef,
  onRetryMessage
}) => {
  const internalMessagesEndRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = externalMessagesEndRef || internalMessagesEndRef;

  return (
    <ScrollArea className="h-full w-full">
      <div className="flex flex-col items-stretch px-4 py-4 overflow-x-hidden">
        {messages.map((message, index) => {
          const previousMessage = index > 0 ? messages[index - 1] : null;
          
          return (
            <div key={message.id} className="mb-3 w-full flex flex-col min-w-0">
              {message.isUser ? (
                <UserMessage message={message} onViewDocument={onViewDocument} />
              ) : (
                <BotMessage 
                  message={message as ChatMessage & { 
                    sources?: Source[], 
                    changes?: string, 
                    completedThoughts?: any[],
                    agentTimelineSteps?: ChatStep[],
                    changesStatus?: 'applied' | 'cancelled' | 'accepted' | 'rejected' | null,
                    changesUpdatedAt?: Date | null,
                    streamedFinalText?: string,
                    finalResponseComplete?: boolean,
                    finalResponseTraceId?: string | null,
                    finalChunkCount?: number,
                    lastChunkSequence?: number | null
                  }} 
                  onFeedback={onFeedback}
                  onUpdateChangesStatus={onUpdateChangesStatus}
                  onViewDocument={onViewDocument}
                  onRetry={onRetryMessage}
                  previousMessage={previousMessage}
                />
              )}
            </div>
          );
        })}
        
        {isLoading && !streamingMessage && messages.length > 0 && 
         !messages.some(msg => msg.id === 'agent-streaming' || msg.id === 'streaming-complete') && (
          <div className="mb-3 w-full flex justify-start">
            <TypingIndicator currentThoughts={currentStreamingThoughts} />
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
};

export default ChatMessages;
